import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useInboxMutations } from '@/hooks/use-inboxes';
import {
  DestinationPicker,
  type PickedDestination
} from './destination-picker';
import type { BusinessUnit, Inbox } from '@/types';

// No channel type to choose. An inbox created here is always backed by a Channel::Api,
// which is what every helpdesk integration already opens its inbox as, and it is the only
// candy channel needing no operator input. A native Email or Web-widget inbox is created
// in candy, where the Rails model owns its validations and callbacks.
//
// The destination is chosen here and nowhere else, because it lives on the inbox's own
// channel row: `inboxes.channel_id` is a single column, so an inbox answers for exactly
// one destination and cannot be given a second.
//
// It CAN be changed, though, by opening this dialog on an existing inbox. Rewriting one
// key on the channel row is a far smaller act than destroying an inbox that conversations
// already point at, which is why the ordinary undo is an edit. Delete exists beside it for
// the rarer case where the inbox itself should not be there, and machine refuses it while
// anything still references the row.
const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255)
});

type FormData = z.infer<typeof schema>;

interface Props {
  /** The unit the new inbox is assigned to on creation. */
  businessUnit: BusinessUnit;
  /** Editing an existing inbox rather than creating one. */
  inbox?: Inbox;
  trigger: React.ReactNode;
}

export function InboxDialog({ businessUnit, inbox, trigger }: Props) {
  const { createInbox, updateInbox } = useInboxMutations();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [destination, setDestination] = useState<PickedDestination | null>(null);
  const isEdit = inbox != null;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' }
  });

  useEffect(() => {
    if (!open) return;
    reset({ name: inbox?.name ?? '' });
    // `channel_type` is nullable on a stored destination — a row mapped before the type
    // was recorded has none — but the picker needs one to preselect. Email is the type
    // every provider has, and machine revalidates whatever is sent back.
    const stored = inbox?.destination;
    setDestination(
      stored
        ? {
            provider: stored.provider,
            channel_type: stored.channel_type ?? 'email',
            identifier: stored.identifier,
            ...(stored.label ? { label: stored.label } : {})
          }
        : null
    );
  }, [open, reset, inbox]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      if (isEdit) {
        // `provider: null` is how a destination is cleared. Sending the fields absent
        // would mean "leave it alone", which is the opposite of what an operator who just
        // emptied the picker is asking for.
        //
        // ONLY WHEN THERE WAS ONE TO CLEAR. An empty picker on an inbox that never had a
        // destination is not a clearing act, it is the ordinary state of a provider inbox:
        // the one a helpdesk connect creates has no destination by design and still gets an
        // Edit button. Sending `provider: null` for it made opening Edit and pressing Save
        // without changing anything null the channel's `webhook_url` on machine's side, and
        // candy stopped delivering that inbox's webhooks. A rename must stay a rename.
        const clearingDestination = destination == null && inbox.destination != null;
        await updateInbox(inbox.id, {
          name: data.name,
          ...(destination ?? (clearingDestination ? { provider: null } : {}))
        });
        toast.success('Inbox updated');
      } else {
        await createInbox({
          name: data.name,
          bu_id: businessUnit.id,
          ...(destination ?? {})
        });
        toast.success(`Inbox created under ${businessUnit.name}`);
      }
      setOpen(false);
    } catch (err) {
      // A 409 from machine means the destination is already on another inbox. Its message
      // names that inbox, so relay it rather than replacing it with something vaguer.
      toast.error(
        err instanceof Error
          ? err.message
          : isEdit
            ? 'Could not update inbox'
            : 'Could not create inbox'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${inbox.name}` : `Add an inbox to ${businessUnit.name}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Change the name, or point this inbox at a different destination. Clearing the destination leaves the inbox in place and stops anything routing to it.'
              : 'An inbox is what a ticket is routed to. Point it at a destination so tickets arriving there are traced back to this business unit.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='inbox-name'>Name</Label>
            <Input
              id='inbox-name'
              autoFocus
              placeholder='e.g. Maddle — Support'
              {...register('name')}
            />
            {errors.name && (
              <p className='text-xs text-destructive'>{errors.name.message}</p>
            )}
          </div>

          <DestinationPicker
            value={destination}
            onChange={setDestination}
            {...(inbox ? { ownerInboxId: inbox.id } : {})}
          />

          <DialogFooter className='sm:justify-between'>
            {/* Optional on purpose: an inbox can be provisioned now and pointed at a
                destination later by editing it, and leaving the choice mandatory would
                block provisioning whenever a helpdesk token is down. */}
            <p className='self-center text-xs text-muted-foreground'>
              {destination
                ? `Will answer for ${destination.label || destination.identifier}`
                : 'No destination — nothing will route here yet.'}
            </p>
            <Button type='submit' disabled={saving}>
              {saving
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : 'Add inbox'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
